import type { ReactNode } from "react";

/**
 * Placeholder is the one loading placeholder (ADR-0068): a block that holds
 * the space its data is about to fill, with a sweep that runs the way the text
 * will run.
 *
 * It carries the effect and nothing else. The *shape* — how many boxes, how
 * tall, in what arrangement — stays beside the screen that is waiting, because
 * that is the only place that knows what it is about to draw. There is no
 * catalogue of skeletons here and nothing to configure: pass the size and the
 * radius as classes, the way every other box on the page gets them.
 *
 * Hidden from assistive technology. A screen reader is told the region is busy
 * once, by `Pending`; being told it eight more times by the boxes themselves
 * is noise, and the boxes mean nothing without the layout they imply.
 */
export default function Placeholder({ className = "" }: { className?: string }) {
  return <div aria-hidden="true" className={`shimmer ${className}`} />;
}

/**
 * Pending wraps a screen's placeholders and is what actually says "waiting":
 * `aria-busy`, and a label naming what is coming rather than the fact that
 * something is.
 *
 * Only ever around a first load (ADR-0068). Data already on screen is never
 * swapped back for boxes — clients watch work by polling, and a refetch that
 * flashes the page grey is worse than a number a moment out of date.
 */
export function Pending({
  label,
  className,
  testID,
  children,
}: {
  label: string;
  className?: string;
  testID?: string;
  children: ReactNode;
}) {
  return (
    <div aria-busy="true" aria-label={label} data-testid={testID} className={className}>
      {children}
    </div>
  );
}

/**
 * TextLines is the one shape that is not a screen's own: lines of prose.
 *
 * It is here because three screens wait for prose — a chapter, a book's
 * description, a character's arc — and a paragraph's ragged right edge is the
 * detail that makes boxes read as text rather than as a broken layout. The
 * last line of each paragraph is short, and the widths vary, because a stack
 * of identical full-width bars reads as a table.
 */
export function TextLines({ lines = 6, className = "" }: { lines?: number; className?: string }) {
  // Widths cycle rather than being random: a placeholder that reshuffles on
  // every render flickers while it waits.
  const widths = ["w-full", "w-11/12", "w-full", "w-10/12", "w-full", "w-8/12"];
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: lines }, (_, i) => (
        <Placeholder key={i} className={`h-4 rounded-full ${widths[i % widths.length]}`} />
      ))}
    </div>
  );
}
